import React, { useEffect, useRef, useState } from "react";
import { PackageSearch, ClipboardCheck, Warehouse, Truck, ShieldCheck, MapPinned } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { ScrollTrigger, canRunHeavyMotion, scrollToTarget, getLenis } from "@/lib/motion";
import RouteMapFallback from "./RouteMapFallback";
import { CHAPTER_LABELS } from "./geo";

const ICONS = [ClipboardCheck, Warehouse, Truck, ShieldCheck, MapPinned];
const LABEL_KEYS = ["yiwu", "guangzhou", "istanbul", "gaziantep", "aleppo", "damascus", "latakia"];

function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") || c.getContext("webgl"));
  } catch {
    return false;
  }
}

export default function JourneySection() {
  const { t, lang } = useLang();
  const j = t.journey;
  const [enhanced] = useState(() => typeof window !== "undefined" && canRunHeavyMotion() && hasWebGL());
  return enhanced ? <PinnedJourney j={j} cities={t.cities} lang={lang} /> : <StaticJourney j={j} cities={t.cities} />;
}

/* ───────────────────────────────────────────── Scroll-driven 3D version */
function PinnedJourney({ j, cities, lang }) {
  const root = useRef(null);
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const labelRefs = useRef({});
  const railRef = useRef(null);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);
  const [ready, setReady] = useState(false);
  const count = j.chapters.length;

  // Lazy-load three.js only when the section approaches the viewport.
  useEffect(() => {
    let disposed = false;
    const io = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting || sceneRef.current) return;
        io.disconnect();
        try {
          const { default: RouteScene } = await import(/* webpackChunkName: "route-scene" */ "./RouteScene");
          if (disposed || !canvasRef.current) return;
          const scene = new RouteScene(canvasRef.current, {
            onLabels: (pos) => {
              const show = CHAPTER_LABELS[activeRef.current] || [];
              const box = canvasRef.current?.parentElement?.getBoundingClientRect();
              const placed = [];
              // Chapter order = priority: a label that would overlap one already
              // placed, or poke out of the map area, waits for a better angle.
              const order = [...show, ...LABEL_KEYS.filter((k) => !show.includes(k))];
              order.forEach((key) => {
                const el = labelRefs.current[key];
                const p = pos[key];
                if (!el || !p) return;
                el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) translate(-50%, -160%)`;
                const w = el.offsetWidth;
                const h = el.offsetHeight;
                const rect = { l: p.x - w / 2, r: p.x + w / 2, t: p.y - h * 1.6, b: p.y - h * 0.6 };
                const inside = !box || (rect.t > 4 && rect.l > 4 && rect.r < box.width - 4);
                const clear = placed.every((o) => rect.r < o.l || rect.l > o.r || rect.b < o.t || rect.t > o.b);
                const on = p.visible && show.includes(key) && inside && clear;
                if (on) placed.push(rect);
                el.style.opacity = on ? "1" : "0";
              });
            },
          });
          sceneRef.current = scene;
          const st = ScrollTrigger.getById("journey");
          scene.setProgress(st ? st.progress : 0);
          if (ScrollTrigger.getById("journey-visible")?.isActive) scene.start();
          setReady(true);
        } catch {
          /* WebGL failed: the poster map stays visible. */
        }
      },
      { rootMargin: "800px 0px" },
    );
    io.observe(root.current);

    const onResize = () => sceneRef.current?.resize();
    window.addEventListener("resize", onResize);
    return () => {
      disposed = true;
      io.disconnect();
      window.removeEventListener("resize", onResize);
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, []);

  // Scroll → progress. The render loop runs only while the section is on screen.
  useEffect(() => {
    const st = ScrollTrigger.create({
      id: "journey",
      trigger: root.current,
      start: "top top",
      end: "bottom bottom",
      onUpdate: (self) => {
        const p = self.progress;
        sceneRef.current?.setProgress(p);
        if (railRef.current) railRef.current.style.setProperty("--p", p.toFixed(4));
        const next = Math.min(count - 1, Math.floor(p * count));
        if (next !== activeRef.current) {
          activeRef.current = next;
          setActive(next);
        }
      },
    });
    const visible = ScrollTrigger.create({
      id: "journey-visible",
      trigger: root.current,
      start: "top bottom",
      end: "bottom top",
      onToggle: (self) => (self.isActive ? sceneRef.current?.start() : sceneRef.current?.stop()),
    });
    return () => {
      st.kill();
      visible.kill();
    };
  }, [count]);

  useEffect(() => {
    ScrollTrigger.refresh();
    sceneRef.current?.resize(); // direction may have flipped (RTL ↔ LTR)
  }, [lang]);

  const jumpTo = (i) => {
    const el = root.current;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const y = top + ((i + 0.5) / count) * (el.offsetHeight - window.innerHeight);
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(y, { duration: 1.4 });
    else window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <section
      ref={root}
      data-testid="journey-section"
      aria-labelledby="journey-title"
      className="on-dark relative bg-[var(--forest-950)]"
      style={{ height: `${count * 100 + 40}svh` }}
    >
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        {/* Map */}
        {/* Desktop: the map fills the stage behind the copy. Mobile: it gets its own
            band between the title and the chapter text, edges faded. */}
        <div
          className="absolute inset-x-0 top-[29%] bottom-[41%] lg:inset-0 [mask-image:linear-gradient(180deg,transparent,black_14%,black_86%,transparent)] lg:[mask-image:none]"
          dir="ltr"
        >
          <RouteMapFallback
            cities={cities}
            className={`absolute inset-0 transition-opacity duration-1000 ${ready ? "opacity-0" : "opacity-60"}`}
          />
          <canvas
            ref={canvasRef}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${ready ? "opacity-100" : "opacity-0"}`}
            aria-hidden
          />
          <div className="absolute inset-0 pointer-events-none" aria-hidden>
            {LABEL_KEYS.map((key) => (
              <span
                key={key}
                ref={(el) => {
                  labelRefs.current[key] = el;
                }}
                className="absolute left-0 top-0 whitespace-nowrap rounded-md bg-[oklch(0.17_0.028_140/0.88)] border border-white/10 px-2.5 py-1 text-[13px] font-semibold text-[var(--on-dark)] opacity-0 transition-opacity duration-500"
              >
                {cities[key]}
              </span>
            ))}
          </div>
        </div>

        {/* Legibility veils */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none hidden lg:block bg-[linear-gradient(180deg,var(--forest-950)_0%,transparent_22%,transparent_70%,var(--forest-950)_100%)]"
        />
        <div
          aria-hidden
          className="lg:hidden absolute inset-x-0 bottom-0 h-[58%] pointer-events-none bg-[linear-gradient(180deg,transparent,oklch(0.17_0.028_140/0.92)_30%,var(--forest-950)_55%)]"
        />
        <div
          aria-hidden
          className="absolute inset-y-0 start-0 w-[55%] pointer-events-none hidden lg:block bg-[linear-gradient(90deg,oklch(0.17_0.028_140/0.92),transparent)] rtl:bg-[linear-gradient(270deg,oklch(0.17_0.028_140/0.92),transparent)]"
        />

        <div className="container-x relative h-full flex flex-col pt-24 lg:pt-28 pb-8">
          <div className="max-w-xl">
            <p className="text-[15px] font-semibold text-[var(--signal)]">{j.kicker}</p>
            <h2 id="journey-title" className="type-display mt-3 text-[clamp(1.9rem,3.6vw,3rem)] text-[var(--on-dark)]">
              {j.title}
            </h2>
          </div>

          {/* Chapters: stacked; the active one is in view */}
          <div className="relative flex-1 mt-6">
            {j.chapters.map((c, i) => {
              const Icon = ICONS[i];
              const on = i === active;
              return (
                <div key={`${lang}-${i}`} className="absolute inset-x-0 bottom-6 lg:bottom-auto lg:top-1/2 lg:-translate-y-1/2">
                  <article
                    aria-hidden={!on}
                    className={`max-w-md transition-[opacity,transform,filter] duration-700 ease-out-expo ${
                      on ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-6 blur-sm pointer-events-none"
                    }`}
                  >
                    <div className="flex items-center gap-3 text-[var(--on-dark-muted)]">
                      <span className="w-10 h-10 rounded-xl grid place-items-center bg-[var(--signal)] text-[var(--signal-ink)]">
                        <Icon className={`w-5 h-5 ${i === 2 && lang === "ar" ? "rtl-flip" : ""}`} aria-hidden />
                      </span>
                      <span className="type-num text-sm font-semibold">
                        {i + 1} / {count} · {c.k}
                      </span>
                    </div>
                    <h3 className="type-display mt-4 lg:mt-5 text-[clamp(1.7rem,4.4vw,3.5rem)] text-[var(--on-dark)]">{c.t}</h3>
                    <p className="mt-3 lg:mt-4 text-base lg:text-lg leading-relaxed text-[var(--on-dark-muted)] max-w-[40ch]">{c.d}</p>
                    {i === count - 1 && (
                      <button
                        type="button"
                        onClick={() => scrollToTarget("#track")}
                        className="btn-primary mt-5 lg:mt-7 h-12 px-6 rounded-full gap-2"
                        tabIndex={on ? 0 : -1}
                      >
                        <PackageSearch className="w-5 h-5" aria-hidden /> {j.cta}
                      </button>
                    )}
                  </article>
                </div>
              );
            })}
          </div>

          {/* Progress rail */}
          <nav ref={railRef} aria-label={j.progress} className="relative grid grid-cols-5 gap-2 sm:gap-3 [--p:0]">
            {j.chapters.map((c, i) => (
              <button
                key={`${lang}-rail-${i}`}
                type="button"
                onClick={() => jumpTo(i)}
                aria-current={i === active ? "step" : undefined}
                aria-label={c.k}
                className="group text-start py-2"
              >
                <span className="block h-[3px] rounded-full bg-white/15 overflow-hidden">
                  <span
                    className="block h-full bg-[var(--signal)] origin-left rtl:origin-right"
                    style={{ transform: `scaleX(clamp(0, calc(var(--p) * ${count} - ${i}), 1))` }}
                  />
                </span>
                <span
                  className={`mt-2.5 hidden sm:block text-xs font-semibold transition-colors ${
                    i === active ? "text-[var(--on-dark)]" : "text-[var(--on-dark-muted)] group-hover:text-[var(--on-dark)]"
                  }`}
                >
                  {c.k}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────── Static version */
function StaticJourney({ j, cities }) {
  return (
    <section data-testid="journey-section" aria-labelledby="journey-title" className="on-dark bg-[var(--forest-950)] py-24 lg:py-32">
      <div className="container-x">
        <p className="text-[15px] font-semibold text-[var(--signal)]">{j.kicker}</p>
        <h2 id="journey-title" className="type-display mt-3 text-[clamp(2rem,4.4vw,3.5rem)] text-[var(--on-dark)] max-w-[18ch]">
          {j.title}
        </h2>
        <div dir="ltr">
          <RouteMapFallback cities={cities} className="mt-10 aspect-[16/10] lg:aspect-[21/9] rounded-3xl border border-white/10 overflow-hidden" />
        </div>
        <ol className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
          {j.chapters.map((c, i) => {
            const Icon = ICONS[i];
            return (
              <li key={c.t}>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl grid place-items-center bg-[var(--signal)] text-[var(--signal-ink)]">
                    <Icon className="w-5 h-5" aria-hidden />
                  </span>
                  <span className="type-num text-sm font-semibold text-[var(--on-dark-muted)]">
                    {i + 1} · {c.k}
                  </span>
                </div>
                <h3 className="mt-4 text-xl font-extrabold text-[var(--on-dark)]">{c.t}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[var(--on-dark-muted)]">{c.d}</p>
              </li>
            );
          })}
        </ol>
        <button type="button" onClick={() => scrollToTarget("#track")} className="btn-primary mt-12 h-12 px-6 rounded-full gap-2">
          <PackageSearch className="w-5 h-5" aria-hidden /> {j.cta}
        </button>
      </div>
    </section>
  );
}
